import { Box, Typography } from '@mui/material'
import { themeColors } from '../../app/layout/theme'
import { BreadcrumbNav, BreadcrumbItem } from './BreadcrumbNav'

// Re-export so callers that import BreadcrumbItem from PageHeader keep working
export type { BreadcrumbItem }

export interface PageHeaderStat {
  label: string
  value: string | number
  onClick?: () => void
  active?: boolean
  color?: string
  icon?: React.ReactNode
}

interface PageHeaderProps {
  title: string
  subtitle?: string
  /**
   * Pass an array of parent crumbs, e.g.
   *   breadcrumb={[{ label: 'Configuration', to: '/settings?tab=admin' }]}
   * The current `title` is automatically appended as the last crumb.
   */
  breadcrumb?: string | BreadcrumbItem[]
  /**
   * Set to true to render the breadcrumb trail below the title.
   * Defaults to false — no breadcrumb is shown unless opted in.
   */
  showBreadcrumb?: boolean
  icon?: React.ReactNode
  actions?: React.ReactNode
  titleWeight?: number | string
  stats?: PageHeaderStat[]
  /** Optional content rendered inline, immediately to the right of the title (e.g. a LOB selector). */
  titleExtra?: React.ReactNode
}

export const PageHeader = ({
  title,
  subtitle,
  icon,
  actions,
  titleWeight,
  breadcrumb,
  showBreadcrumb = false,
  stats,
  titleExtra,
}: PageHeaderProps) => {
  // Normalise breadcrumb prop into BreadcrumbItem[] only when opted in
  let breadcrumbItems: BreadcrumbItem[] = []
  if (showBreadcrumb) {
    if (typeof breadcrumb === 'string') {
      breadcrumbItems = [{ label: breadcrumb }]
    } else if (Array.isArray(breadcrumb)) {
      breadcrumbItems = breadcrumb
    }
  }

  return (
    <Box sx={{ px: 3, pt: 2.5, pb: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, flexWrap: 'wrap', gap: 2 }}>
        {/* Left stack: either large breadcrumbs OR icon + title, and metrics */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            {showBreadcrumb ? (
              <BreadcrumbNav
                items={breadcrumbItems}
                current={title}
                showBack={true}
                fontSize="16px"
              />
            ) : (
              <>
                {icon && (
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      background: themeColors.primary,
                      '&:hover': { background: themeColors.primaryDark },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 2px 8px rgba(0,112,210,0.25)',
                      '& svg': { fontSize: 20, color: '#fff' },
                    }}
                  >
                    {icon}
                  </Box>
                )}

                <Box>
                  {/* Page title (+ optional inline extra, e.g. a LOB selector) */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: titleWeight ?? 700,
                        fontSize: '20px',
                        color: themeColors.text.primary,
                        lineHeight: 1.2,
                        letterSpacing: '-0.3px',
                      }}
                    >
                      {title}
                    </Typography>
                    {titleExtra}
                  </Box>

                  {/* Subtitle (optional) */}
                  {subtitle && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: themeColors.text.secondary,
                        mt: '4px',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {subtitle}
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          color: themeColors.text.secondary,
                          '&:hover': { color: themeColors.primary },
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7 10l5 5 5-5z" />
                        </svg>
                      </Box>
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>

          {/* Metric Stats Cards as Buttons */}
          {stats && stats.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', ml: { xs: 0, md: 2 } }}>
              {stats.map((stat, idx) => {
                const hasClick = !!stat.onClick
                return (
                  <Box
                    key={idx}
                    component={hasClick ? 'button' : 'div'}
                    onClick={stat.onClick}
                    sx={{
                      border: '1px solid',
                      borderColor: stat.active ? themeColors.primary : themeColors.border,
                      borderRadius: '4px',
                      bgcolor: stat.active ? `${themeColors.primary}0a` : '#fff',
                      px: 1.5,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: hasClick ? 'pointer' : 'default',
                      transition: 'all 0.15s ease-in-out',
                      outline: 'none',
                      fontFamily: 'inherit',
                      textAlign: 'left',
                      boxShadow: 'none',
                      '&:hover': hasClick ? {
                        borderColor: themeColors.primary,
                        bgcolor: stat.active ? `${themeColors.primary}0f` : `${themeColors.primary}04`
                      } : {},
                      '&:active': hasClick ? {
                        bgcolor: stat.active ? `${themeColors.primary}12` : `${themeColors.primary}08`
                      } : {}
                    }}
                  >
                    {stat.icon && (
                      <Box sx={{ display: 'flex', color: stat.color || themeColors.primary, opacity: 0.9 }}>
                        {stat.icon}
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: themeColors.text.secondary,
                          textTransform: 'uppercase',
                          letterSpacing: '0.4px',
                          lineHeight: 1
                        }}
                      >
                        {stat.label}:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: stat.active ? themeColors.primary : themeColors.text.primary,
                          lineHeight: 1
                        }}
                      >
                        {stat.value}
                      </Typography>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          )}
        </Box>

        {/* Right: action buttons */}
        {actions && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: '2px', flexShrink: 0 }}>
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  )
}
